import {AsyncPipe, TitleCasePipe} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MatTable, MatTableDataSource, MatTableModule} from '@angular/material/table';
import {BehaviorSubject} from 'rxjs';

import {OphAvatarComponent} from '../../shared/design/oph-avatar/oph-avatar.component';
import {OphPaginatorComponent} from '../../shared/design/oph-paginator/oph-paginator.component';
import {ListViewsModule} from '../../shared/list-views/list-views.module';
import {Team} from '../shared/model/team';
import {TeamDialogComponent, TeamDialogData} from '../team-dialog/team-dialog.component';
import {TeamsMembersAvatarsComponent} from '../teams-members-avatars/teams-members-avatars.component';
import {TEAM_DIALOG_SETTINGS} from '../utils/teams.constants';
import {MobileService} from 'src/app/core/page/mobile.service';

@Component({
  selector: 'teams-table',
  standalone: true,
  imports: [
    AsyncPipe,
    OphPaginatorComponent,
    MatTableModule,
    ListViewsModule,
    OphAvatarComponent,
    TitleCasePipe,
    TeamsMembersAvatarsComponent,
  ],
  templateUrl: './teams-table.component.html',
  styleUrl: './teams-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TeamsTableComponent implements OnInit, OnChanges, OnDestroy {
  private readonly mobileService = inject(MobileService);
  private dialog = inject(MatDialog);

  displayedColumns: string[] = ['name', 'members'];
  dataSource = new MatTableDataSource<Team>([]);
  dataSource$ = new BehaviorSubject<Team[]>([]);
  paginatedDataSource = signal<Team[]>([]);
  @ViewChild('teamsTableEl') teamsTableEl: MatTable<Team>;

  searchValue$ = input<BehaviorSubject<string>>();
  allTeams = input.required<Team[]>();

  menuOptions = ['edit', 'delete'];
  singleUserMenuOptions = ['edit'];
  pageSize = signal<number>(10);
  totalPages = signal<number>(0);
  currentPage = 0;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.allTeams) {
      this.setTeams();
    }
  }

  ngOnInit() {
    this.setTeams();
    this.subscribeToDataSource();
    this.subscribeToSearchValue();
  }

  ngOnDestroy(): void {
    if (this.dataSource$) {
      this.dataSource$?.unsubscribe();
    }
  }

  setTeams(): void {
    this.updatePaginatedData();
  }

  subscribeToDataSource() {
    this.dataSource$.asObservable().subscribe(teams => {
      this.dataSource.data = teams;
    });
  }

  subscribeToSearchValue(): void {
    this.searchValue$().subscribe((searchValue: string) => {
      this.currentPage = 0;
      this.dataSource.data = this.allTeams();
      this.dataSource.filter = searchValue.trim().toLowerCase();
      this.updatePaginatedData();
    });
  }

  updatePaginatedData() {
    // Get the total number of items based on filter
    const totalItems =
      this.dataSource.filter.length > 0 ? this.dataSource.filteredData.length : this.allTeams()?.length || 0;

    // If we're on the last page and it has fewer items than pageSize, move to previous page
    if (this.currentPage > 0 && this.currentPage * this.pageSize() >= totalItems) {
      this.currentPage = Math.floor((totalItems - 1) / this.pageSize());
    }

    const startIndex = this.currentPage * this.pageSize();
    const endIndex = startIndex + this.pageSize();

    if (!this.dataSource.filter.length) {
      this.dataSource$.next(this.allTeams()?.slice(startIndex, endIndex) || []);
    } else {
      this.dataSource$.next(this.dataSource.filteredData.slice(startIndex, endIndex));
    }

    // Refresh the table
    if (this.teamsTableEl) {
      this.teamsTableEl.renderRows();
    }
    this.getTotalPages();
  }

  getTotalPages(): void {
    if (this.dataSource.filter.length > 0) {
      const totalPages = Math.ceil(this.dataSource.filteredData.length / this.pageSize());
      this.totalPages.set(totalPages);
    } else {
      this.totalPages.set(Math.ceil(this.allTeams()?.length / this.pageSize()));
    }
  }

  onPageChange(page: number) {
    this.currentPage = page - 1;
    this.updatePaginatedData();
  }

  onTeamClick(team: Team) {
    this.dialog.open(TeamDialogComponent, {
      data: {team: team, teams: this.allTeams()} as TeamDialogData,
      ...TEAM_DIALOG_SETTINGS,
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
    });
  }

  trackByFn(index: number, team: Team) {
    if (!team?.id) return index;
    return team?.id;
  }
}
